import { z } from 'zod'
import { organizerEventSchema, organizerKindValues } from './channels'

/** Where the viewer stands with an organizer: not a member, asked, a member, or turned down. */
export const membershipStatusValues = ['none', 'pending', 'active', 'declined'] as const
export type MembershipStatus = (typeof membershipStatusValues)[number]

/** The secret of a member invite link (`/m/{token}`). */
export const memberInviteTokenSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]{24,64}$/)

/** GET /api/channels/{id}/membership: the viewer's membership and what it opens up. */
export const myMembershipSchema = z.object({
  channelId: z.string().uuid(),
  membersEnabled: z.boolean(),
  status: z.enum(membershipStatusValues),
  memberCount: z.number().int(),
  /** After a declined request: when the viewer may ask again; null when they may ask now. */
  requestAgainAt: z.string().nullable(),
  /** The upcoming members-only events; empty unless the viewer is an active member. */
  events: z.array(organizerEventSchema),
})

export type MyMembership = z.infer<typeof myMembershipSchema>

/** GET /api/member-invites/{token}: the organizer behind an invite link. */
export const memberInvitePreviewSchema = z.object({
  channel: z.object({
    id: z.string().uuid(),
    handle: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    kind: z.enum(organizerKindValues),
    isVerified: z.boolean(),
  }),
  memberCount: z.number().int(),
  /** The signed-in viewer's membership; `none` for visitors. */
  status: z.enum(membershipStatusValues),
})

export type MemberInvitePreview = z.infer<typeof memberInvitePreviewSchema>

/** One organizer the viewer belongs to or asked to join. */
export const myMembershipEntrySchema = z.object({
  channelId: z.string().uuid(),
  handle: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  status: z.enum(['pending', 'active']),
  since: z.string(),
})

export const myMembershipsResponseSchema = z.object({
  memberships: z.array(myMembershipEntrySchema),
})

export type MyMembershipEntry = z.infer<typeof myMembershipEntrySchema>
export type MyMembershipsResponse = z.infer<typeof myMembershipsResponseSchema>

/** A member or a waiting request, as only the organizer's dashboard sees it. */
export const organizerMemberSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  displayName: z.string(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  status: z.enum(['pending', 'active']),
  requestedAt: z.string(),
  /** When the person became a member; null while the request waits. */
  since: z.string().nullable(),
})

export type OrganizerMember = z.infer<typeof organizerMemberSchema>

export const organizerMembersQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
})

/** GET /api/businesses/{businessId}/channels/{channelId}/members */
export const organizerMembersResponseSchema = z.object({
  membersEnabled: z.boolean(),
  /** The member invite link; null until the organizer creates one. */
  inviteUrl: z.string().nullable(),
  counts: z.object({ pending: z.number().int(), active: z.number().int() }),
  pending: z.array(organizerMemberSchema),
  active: z.array(organizerMemberSchema),
})

export type OrganizerMembersResponse = z.infer<typeof organizerMembersResponseSchema>

export const updateMemberSettingsRequestSchema = z.object({
  membersEnabled: z.boolean(),
})

export const memberInviteLinkResponseSchema = z.object({
  url: z.string(),
})

export type MemberInviteLinkResponse = z.infer<typeof memberInviteLinkResponseSchema>

export const approveAllMembersResponseSchema = z.object({
  approved: z.number().int(),
})
